import type { ErrorEvent } from '@sentry/nextjs';
import { scrubEvent, sentryCommonOptions } from '@/lib/sentry';

// DOM-Detach-Diagnose (Sentry CHECKPOINT-EDU-1A, 2026-08-31): Die
// removeChild/insertBefore-Fehlerklasse entsteht, wenn Fremdsoftware
// (Extension, Content-Blocker, MDM-Filter) Knoten entfernt, die React
// verwaltet — im untersuchten Fall ein gehoistetes Head-Element. Aus dem
// Stacktrace allein ist nie ablesbar, WELCHER Knoten fehlte; das
// Head-Inventar zum Crash-Zeitpunkt beantwortet das beim nächsten Event in
// einem Schritt. Nur Tag + Schlüssel-Attribut, gedeckelt — keine Inhalte,
// keine Personendaten.
const DOM_DETACH_PATTERN = /removeChild|insertBefore/;

function headInventory(): string[] {
  return Array.from(document.head.children)
    .slice(0, 40)
    .map((el) => {
      const key =
        el.getAttribute('name') ??
        el.getAttribute('property') ??
        el.getAttribute('rel') ??
        el.getAttribute('http-equiv');
      return key ? `${el.tagName.toLowerCase()}[${key}]` : el.tagName.toLowerCase();
    });
}

function scrubAndEnrich(event: ErrorEvent): ErrorEvent {
  const scrubbed = scrubEvent(event);
  const message = scrubbed.exception?.values?.[0]?.value ?? '';
  if (DOM_DETACH_PATTERN.test(message)) {
    try {
      scrubbed.extra = { ...scrubbed.extra, head_inventory: headInventory() };
    } catch {
      // Das Inventar ist Diagnose-Bonus — nie den Versand blockieren.
    }
  }
  return scrubbed;
}

// Client-seitige Sentry-Initialisierung (P2). Dormant ohne NEXT_PUBLIC_SENTRY_DSN.
// Dynamischer Import statt `import * as Sentry`: der statische Import zog das
// komplette Client-SDK (~130 KB gzip, grösster Shared-Chunk) in den First-Load
// JEDER Route (Perf-Audit 2026-07-16, R3). Trade-off: Fehler in den ersten
// Millisekunden vor Modul-Load entgehen Sentry — akzeptiert.
const dsn = process.env.NEXT_PUBLIC_SENTRY_DSN;
if (dsn) {
  import('@sentry/nextjs')
    .then((Sentry) => {
      Sentry.init({
        dsn,
        environment: process.env.NEXT_PUBLIC_VERCEL_ENV ?? 'production',
        ...sentryCommonOptions,
        // Überschreibt das beforeSend aus sentryCommonOptions: gleiche
        // Scrub-Logik, plus Head-Inventar für die DOM-Detach-Klasse (oben).
        beforeSend: scrubAndEnrich,
      });
    })
    .catch(() => {});
}
